import { type MutableRefObject, useRef } from 'react';
import { useIsomorphicLayoutEffect } from 'react-use';

type UseWorkspaceAgentComposerSlotReservedHeightParams = {
  isConsentDockOpen: boolean;
};

type UseWorkspaceAgentComposerSlotReservedHeightResult = {
  composerSlotMinHeight: number;
  composerSlotRef: MutableRefObject<HTMLDivElement | null>;
};

/**
 * Holds the closed composer slot's height (including its `pb-3`) as a min height while a consent
 * dock is open. Choice docks hide the composer and drop that padding, and the dock card is a pixel
 * or two shorter than the slot it replaces, so without this the thread drops as the viewport grows.
 * A taller dock still grows the slot.
 */
export const useWorkspaceAgentComposerSlotReservedHeight = ({
  isConsentDockOpen,
}: UseWorkspaceAgentComposerSlotReservedHeightParams): UseWorkspaceAgentComposerSlotReservedHeightResult => {
  const composerSlotRef = useRef<HTMLDivElement | null>(null);
  const reservedComposerSlotHeightRef = useRef(0);

  useIsomorphicLayoutEffect(() => {
    if (isConsentDockOpen) {
      return;
    }

    // Measured every commit while the dock is closed, including after the composer clears on
    // send, so the open render reads the height the slot had just before it lost `pb-3`.
    const height = composerSlotRef.current?.offsetHeight;

    if (!height) {
      return;
    }

    reservedComposerSlotHeightRef.current = height;
  });

  return {
    composerSlotMinHeight: isConsentDockOpen ? reservedComposerSlotHeightRef.current : 0,
    composerSlotRef,
  };
};
